import styled from "@emotion/styled";
import Message from "../../components/icons/Message";
import Review from "../../components/icons/Review";
import { ParagraphMedium } from "../../components/typography/Paragraph";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import { Header } from "../../components/typography/Header";
import {
  StyledAccessBlockerContainer,
  StyledAccessBlockerButton,
} from "../../components/layout/utils/desktop/components";
import { useUpdateDocumentMutation } from "../../api/documents";
import RegenerateDocumentPopup from "../../components/popups/RegenerateDocumentPopup";
import { isDateInFuture } from "../../utils/dateUtils";
import { useQuestionnaireSectionState } from "../../hooks/useQuestionnaireSectionState";
import {
  EventFilter,
  EventStatus,
  ProductType,
} from "../../../../api/src/types";
import { useHasEvent } from "../../hooks/useHasEvent";

const StyledHeader = styled(Header)`
  margin-bottom: 16px;
`;

const getAccessBlockerContent = (
  isNotarizationComplete: boolean,
  isDocumentInProof: boolean,
  isDocumentInEditor: boolean,
  isDocumentReviewed: boolean,
  isDocumentFinalized: boolean,
  isDocumentGenerated: boolean,
  blockedByFutureRepresentationEvent: boolean,
): AccessBlockerContent | null => {
  // Priority order for access blocker content (highest to lowest priority):
  // 1. Notarization Complete - Document is fully notarized - final
  // 2. Document In Proof - Document is in the notarization process with Proof - locked
  // 3. Document Finalized - Document has been finalized and signed - view button
  // 4. Document Edited - Document is currently being edited by attorney - go to editor button
  // 5. Document Reviewed - Document has been reviewed and is ready for viewing - view / regenerate button
  if (isNotarizationComplete) {
    return {
      icon: <Review />,
      title: "🎉 All Set! Your Prenup is Notarized",
      description:
        "You've already crossed the finish line—your prenup is notarized and ready to go! That means no more edits can be made.",
    };
  }

  if (isDocumentInProof) {
    return {
      icon: <Review />,
      title: "Your prenup is already in the notarization process",
      description:
        "Your prenup has been sent to Proof to begin the notarization process. At this stage, the document is locked and no further edits can be made.",
    };
  }

  if (isDocumentFinalized) {
    return {
      icon: <Review />,
      title: "🎉 Congratulations!",
      description:
        "You have finalized your prenup! Click on the button below to view your document.",
    };
  }

  if (isDocumentInEditor && !blockedByFutureRepresentationEvent) {
    return {
      icon: <Message />,
      title: "Your document is being edited",
      description: "Click the button below to go to the editor.",
    };
  }

  if (isDocumentReviewed) {
    return {
      icon: <Review />,
      title: "🎉 Congratulations!",
      description:
        "You have generated your prenup! Click on the button below to view your document.",
    };
  }
  if (isDocumentGenerated) {
    return {
      icon: <Review />,
      title: "🎉 Congratulations!",
      description:
        "You have generated your prenup! Click on the button below to view your document.",
    };
  }

  return null;
};

interface QuestionnaireAccessBlockerProps {
  documentId: string;
}

interface AccessBlockerContent {
  icon: ReactNode;
  title: string;
  description: string;
}

const QuestionnaireAccessBlocker = ({
  documentId,
}: QuestionnaireAccessBlockerProps) => {
  const navigate = useNavigate();

  const {
    isNotarizationComplete,
    isDocumentInProof,
    isDocumentFinalized,
    isDocumentInEditor,
    isDocumentReviewed,
    isDocumentGenerated,
    weddingDate,
  } = useQuestionnaireSectionState();

  const { blockedByFutureRepresentationEvent } = useHasEvent(
    ProductType.REPRESENTATION,
    EventFilter.FUTURE,
    EventStatus.BOOKED,
  );

  const [updateDocument] = useUpdateDocumentMutation();

  // Check if wedding date is in the future
  const isWeddingDateFuture = isDateInFuture(weddingDate ?? null);

  const [showRegeneratePopup, setShowRegeneratePopup] = useState(false);

  const handleRegenerateClick = () => {
    setShowRegeneratePopup(true);
  };

  const handleRegenerateConfirmNavigateToQuestionnaire = () => {
    void updateDocument({
      documentId,
      reviewedAt: null,
      lastGeneratedInputHash: null,
      lastGeneratedAt: null,
    });
    setShowRegeneratePopup(false);
  };

  const handleRegenerateCancel = () => {
    setShowRegeneratePopup(false);
  };

  const content = getAccessBlockerContent(
    isNotarizationComplete,
    isDocumentInProof,
    isDocumentInEditor,
    isDocumentReviewed,
    isDocumentFinalized,
    isDocumentGenerated,
    blockedByFutureRepresentationEvent,
  );
  if (!content) {
    return null;
  }

  //   // it should only show max 2 buttons.  first is:
  // if (generated && !ineditor && !finalized && isWeddingDateFuture) {
  //   show regenerate
  // }

  // // second is:
  // if (notarized) {
  //   show go to /notarize
  // } else if (inproof) {
  //   if (either user has attorney) {
  //     show go to editor
  //   } else {
  //     // not sure on this one, I think it should show go to /notarize but not confident on that flow
  //   }
  // } else if (ineditor) {
  //   if (blockedbyfuturerep) {
  //     show go to documents/review
  //   } else {
  //     show go to editor
  //   }
  // } else if (generated) {
  //   show View my Document and button to /documents/review
  //   // get rid of the 'Final' text conditional handling for now its just confusing and not that helpful
  // } else {
  //   // if it wasnt generated this blocker wont be showing up so we don't need anything here I think?
  // }

  return (
    <StyledAccessBlockerContainer>
      {content.icon}
      <StyledHeader>{content.title}</StyledHeader>
      <ParagraphMedium fontWeight={300}>{content.description}</ParagraphMedium>

      {/* Show "View Final Document" button when:
          - Document has been notarized (isNotarizationComplete)
          - This is the final, legally binding version of the document */}
      {isNotarizationComplete && (
        <StyledAccessBlockerButton
          onClick={() => navigate("/notarize")}
          aria-label="View Final Document"
        >
          View Final Document
        </StyledAccessBlockerButton>
      )}

      {/* Show "View Final Document" button when document is ready for viewing:
          - Document has been reviewed by finances (isDocumentReviewed)
          - OR document has been finalized and signed (isDocumentFinalized) */}
      {(isDocumentReviewed || isDocumentFinalized) &&
        !isNotarizationComplete && (
          <StyledAccessBlockerButton
            onClick={() => navigate("/documents/review")}
            aria-label={`View ${blockedByFutureRepresentationEvent ? "" : "Final"} Document`}
          >
            {`View ${blockedByFutureRepresentationEvent ? "" : "Final"} Document`}
          </StyledAccessBlockerButton>
        )}

      {/* Show "Edit Document" button when:
          - Document has been edited (isDocumentInEditor)
          - User can make changes to the document content */}
      {isDocumentInEditor &&
        !isDocumentInProof &&
        !isDocumentFinalized &&
        !blockedByFutureRepresentationEvent && (
          <StyledAccessBlockerButton
            onClick={() => navigate(`/documents/${documentId}/editor`)}
            aria-label="Go to Editor"
          >
            Go to Editor
          </StyledAccessBlockerButton>
        )}

      {/* Show "Regenerate Document" button when:
          - Document was generated  (isDocumentGenerated)
          - AND document has been reviewed (isDocumentReviewed)
          - AND document is not yet notarized */}
      {isDocumentGenerated &&
        isDocumentReviewed &&
        !isDocumentFinalized &&
        !isNotarizationComplete &&
        isWeddingDateFuture && (
          <StyledAccessBlockerButton
            onClick={handleRegenerateClick}
            aria-label="Regenerate Document"
          >
            Regenerate Document
          </StyledAccessBlockerButton>
        )}

      {/* Always Show "Review Document" button when:
          - Document has been generated */}
      {isDocumentGenerated &&
        !isDocumentFinalized &&
        !isNotarizationComplete &&
        isWeddingDateFuture &&
        !isDocumentReviewed && (
          <StyledAccessBlockerButton
            onClick={() => navigate("/documents/review")}
            aria-label="Review Document"
          >
            Review Document
          </StyledAccessBlockerButton>
        )}

      {/* Show regenerate document popup when showRegeneratePopup is true */}
      {showRegeneratePopup && (
        <RegenerateDocumentPopup
          onCancel={handleRegenerateCancel}
          onConfirm={handleRegenerateConfirmNavigateToQuestionnaire}
        />
      )}
    </StyledAccessBlockerContainer>
  );
};

export default QuestionnaireAccessBlocker;
